import { createApi } from '@reduxjs/toolkit/query/react';
import { filteredFilter } from '../../utils/api';
import {properties} from "../../properties.js";
import { authBaseQuery } from '../../app/axiosBaseQuery';
import { PaginationResult, ListRequest } from '../../types/global.d';
import * as Entity from '../types/models/UserSession';
import {createNormalizationMiddleware} from "../../app/normy/normyRtkQuery.ts";

export const USER_SESSION_API_REDUCER_KEY = 'user-sessions';
export const USER_SESSION_PATH = 'user-sessions';

export const userSessionApi = createApi({
    reducerPath: USER_SESSION_API_REDUCER_KEY,
    baseQuery: authBaseQuery(),
    keepUnusedDataFor: 10,
    refetchOnMountOrArgChange: 10,
    refetchOnFocus: true,
    tagTypes: ['UserSession', 'UserSessions'],
    endpoints: (builder) => ({
        getUserSessionList: builder.query<PaginationResult<Entity.UserSession>, ListRequest>({
            query: (request) => {
                const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request))
                return ({
                    url: `/${ USER_SESSION_PATH }?${new URLSearchParams(queryParams).toString()}`,
                    method: 'GET',
                })
            },
            providesTags: ['UserSessions'],
        }),
        getUserSession: builder.query<Entity.UserSession, Entity.PrimaryUserSession>({
            queryFn: async (id, { getState }, extraOptions, baseQuery) => {

                const cachedData = userSessionNormalizer.getObjectById(id.id);

                if (cachedData) {
                    return { data: cachedData };
                } else {
                    const response = await baseQuery({
                        url: `/${ USER_SESSION_PATH }/${id.toPath()}`,
                        method: 'GET',
                    });

                    return response;
                }
            },
            providesTags: ['UserSession'],
        }),
        getUserSessionMultiple: builder.query<Entity.UserSession, Entity.PrimaryUserSession[]>({
            query: (ids) => {
                return ({
                    url: `/${ USER_SESSION_PATH }/ids${Entity.PrimaryUserSession.toQueryParams(ids)}`,
                    method: 'GET',
                })
            },
            providesTags: ['UserSessions'],
        }),
        getUserSessionPresentation: builder.query<PaginationResult<Entity.UserSession>, Entity.PresentationUserSessionRequest>({
            query: (request) => {
              const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request.list))
              return ({
                url: `/${ USER_SESSION_PATH }/presentation?${new URLSearchParams(queryParams).toString()}`,
                method: 'POST',
                data: request.filter,
              })
            },
            providesTags: ['UserSessions'],
        }),
        getUserSessionUser: builder.query<PaginationResult<Entity.UserSession>, Entity.FilterUserRequest>({
            query: (request) => {
                const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request.list))
                return ({
                    url: `/${ USER_SESSION_PATH }/find/user?${new URLSearchParams(queryParams).toString()}`,
                    method: 'POST',
                    data: request.filter,
                })
            },
            providesTags: ['UserSessions'],
        }),
        getUserSessionRefreshToken: builder.query<PaginationResult<Entity.UserSession>, Entity.FilterRefreshTokenRequest>({
            query: (request) => {
                const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request.list))
                return ({
                    url: `/${ USER_SESSION_PATH }/find/refreshToken?${new URLSearchParams(queryParams).toString()}`,
                    method: 'POST',
                    data: request.filter,
                })
            },
            providesTags: ['UserSessions'],
        }),
        
        insertUserSession: builder.mutation<Entity.UserSession, Entity.CreateUserSession>({
            query: (toInsert) => {
                return ({
                    url: `/${ USER_SESSION_PATH }`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                dispatch(
                    userSessionApi.util.upsertQueryData('getUserSession', Entity.PrimaryUserSession.fromId(mutationData.id), mutationData),
                );
            },
            invalidatesTags: ['UserSessions'],
        }),
        insertMultipleUserSession: builder.mutation<Entity.UserSession[], Entity.CreateUserSession[]>({
            query: (toInsert) => {
                return ({
                    url: `/${ USER_SESSION_PATH }/batch`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                for(const item of mutationData) {
                    dispatch(
                            userSessionApi.util.upsertQueryData('getUserSession', Entity.PrimaryUserSession.fromId(item.id), item),
                    );
                }
            },
            invalidatesTags: ['UserSessions'],
        }),
        updateUserSession: builder.mutation<Entity.UserSession, Entity.UpdateEntityUserSession>({
            query: (toUpdate) => {
                return ({
                    url: `/${ USER_SESSION_PATH }/${toUpdate.id.toPath()}`,
                    method: 'PUT',
                    data: toUpdate.data,
                })
            },
            invalidatesTags: ['UserSessions'],
        }),
        updateMultipleUserSession: builder.mutation<Entity.UserSession[], Entity.UpdateEntityUserSession[]>({
            query: (toUpdate) => {
                return ({
                    url: `/${ USER_SESSION_PATH }/batch`,
                    method: 'PUT',
                    data: toUpdate,
                })
            },
            invalidatesTags: ['UserSessions'],
        }),
        deleteUserSession: builder.mutation<Entity.UserSession, Entity.UserSession>({
            query: (toDelete) => {
                return ({
                    url: `/${ USER_SESSION_PATH }/${Entity.PrimaryUserSession.fromEntity(toDelete)?.toPath()}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['UserSessions'],
        }),
        deleteMultipleUserSession: builder.mutation<Entity.UserSession[], Entity.PrimaryUserSession[]>({
            query: (ids) => {
                return ({
                    url: `/${ USER_SESSION_PATH }/batch${Entity.PrimaryUserSession.toQueryParams(ids)}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['UserSessions'],
        }),
    }),
})

export const [userSessionNormalizer, userSessionNormalizerMiddleware] = createNormalizationMiddleware(USER_SESSION_API_REDUCER_KEY, userSessionApi, {
    devLogging: properties.debugLog,
})

export const {
    useGetUserSessionListQuery,
    useGetUserSessionMultipleQuery,
    useGetUserSessionPresentationQuery,
    useGetUserSessionQuery,
    useInsertUserSessionMutation,
    useInsertMultipleUserSessionMutation,
    useUpdateUserSessionMutation,
    useUpdateMultipleUserSessionMutation,
    useDeleteUserSessionMutation,
    useDeleteMultipleUserSessionMutation,
    useGetUserSessionUserQuery,
    useGetUserSessionRefreshTokenQuery,
    
} = userSessionApi
